"use client"

import { cn } from "@/lib/utils"
import { useState } from "react"

interface LayoutProps {
  children: React.ReactNode
  className?: string
}

interface ContainerProps {
  children: React.ReactNode
  size?: "sm" | "md" | "lg" | "xl" | "full"
  className?: string
}

interface SectionProps {
  children: React.ReactNode
  className?: string
  spacing?: "sm" | "md" | "lg" | "xl"
}

interface GridProps {
  children: React.ReactNode
  cols?: 1 | 2 | 3 | 4 | 5 | 6 | 12
  gap?: "sm" | "md" | "lg" | "xl"
  className?: string
}

interface CardProps {
  children: React.ReactNode
  className?: string
  variant?: "default" | "glass" | "elevated" | "cosmic"
  padding?: "sm" | "md" | "lg" | "xl"
}

interface PanelProps {
  children: React.ReactNode
  title?: string
  icon?: string
  collapsible?: boolean
  defaultOpen?: boolean
  className?: string
}

interface SidebarProps {
  children: React.ReactNode
  className?: string
}

interface MainContentProps {
  children: React.ReactNode
  className?: string
}

// Main Layout Component
export function Layout({ children, className }: LayoutProps) {
  return (
    <div className={cn("min-h-screen bg-background font-sans antialiased", className)}>
      {children}
    </div>
  )
}

// Container Component with responsive max-widths
export function Container({ children, size = "xl", className }: ContainerProps) {
  const sizeClasses = {
    sm: "max-w-2xl",
    md: "max-w-4xl",
    lg: "max-w-6xl",
    xl: "max-w-7xl",
    full: "max-w-full"
  }

  return (
    <div className={cn("mx-auto px-4 sm:px-6 lg:px-8", sizeClasses[size], className)}>
      {children}
    </div>
  )
}

// Section Component for consistent spacing
export function Section({ children, className, spacing = "lg" }: SectionProps) {
  const spacingClasses = {
    sm: "py-8",
    md: "py-12",
    lg: "py-16",
    xl: "py-24"
  }

  return (
    <section className={cn(spacingClasses[spacing], className)}>
      {children}
    </section>
  )
}

// Grid Component for responsive layouts
export function Grid({ children, cols = 1, gap = "md", className }: GridProps) {
  const colClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 lg:grid-cols-2",
    3: "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
    6: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
    12: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
  }

  const gapClasses = {
    sm: "gap-4",
    md: "gap-6",
    lg: "gap-8",
    xl: "gap-12"
  }

  return (
    <div className={cn("grid", colClasses[cols], gapClasses[gap], className)}>
      {children}
    </div>
  )
}

// Card Component with multiple variants
export function Card({ children, className, variant = "default", padding = "md" }: CardProps) {
  const variantClasses = {
    default: "bg-card border border-border shadow-sm",
    glass: "bg-card/50 backdrop-blur-sm border border-border/50 shadow-lg",
    elevated: "bg-card border border-border shadow-xl",
    cosmic: "bg-gradient-to-br from-cyan-900/20 to-purple-900/20 backdrop-blur-sm border border-cyan-500/30 shadow-lg"
  }

  const paddingClasses = {
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
    xl: "p-12"
  }

  return (
    <div className={cn("rounded-lg", variantClasses[variant], paddingClasses[padding], className)}>
      {children}
    </div>
  )
}

// Header Component
interface HeaderProps {
  title: string
  subtitle?: string
  className?: string
  centered?: boolean
}

export function Header({ title, subtitle, className, centered = true }: HeaderProps) {
  return (
    <div className={cn("mb-8", centered && "text-center", className)}>
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4 drop-shadow-2xl">
        {title}
      </h1>
      {subtitle && (
        <p className="text-xl md:text-2xl text-muted-foreground font-medium">
          {subtitle}
        </p>
      )}
    </div>
  )
}

// Footer Component
interface FooterProps {
  children: React.ReactNode
  className?: string
}

export function Footer({ children, className }: FooterProps) {
  return (
    <footer className={cn("mt-16 pt-8 border-t border-border", className)}>
      <div className="text-center">
        {children}
      </div>
    </footer>
  )
}

// Panel Component with collapsible functionality
export function Panel({
  children,
  title,
  icon,
  collapsible = false,
  defaultOpen = true,
  className
}: PanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className={cn(
      "group relative",
      // Unified background and glass effect
      "unified-bg-secondary unified-glass-panel",
      "unified-border-panel",
      // Simplified shadows and hover effects
      "shadow-lg shadow-slate-900/10 hover:shadow-xl hover:shadow-slate-900/20",
      "transition-all duration-300 ease-out transform-gpu",
      // Simplified hover effects
      "hover:border-white/15",
      className
    )}>
      {title && (
        <div className="relative flex items-center justify-between unified-padding-panel border-b border-white/10 group-hover:border-white/20 transition-colors duration-300">
          <div className="flex items-center space-x-3">
            {icon && (
              <span className="text-xl group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 ease-out transform-gpu drop-shadow-lg">
                {icon}
              </span>
            )}
            <h3 className="text-lg font-bold text-slate-200 group-hover:text-cyan-300 transition-colors duration-300 drop-shadow-sm">
              {title}
            </h3>
          </div>
          {collapsible && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative flex items-center justify-center w-10 h-10 rounded-xl
                bg-gradient-to-br from-slate-700/40 to-slate-600/20
                border border-slate-500/30
                text-slate-400 hover:text-cyan-300
                hover:bg-gradient-to-br hover:from-cyan-500/20 hover:to-blue-500/15
                hover:border-cyan-400/50 hover:shadow-lg hover:shadow-cyan-500/30
                transition-all duration-300 ease-out
                group/btn active:scale-95 transform-gpu
                before:absolute before:inset-0 before:rounded-xl before:bg-gradient-to-r before:from-cyan-500/10 before:to-transparent
                before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300"
              aria-label={isOpen ? 'Collapse panel' : 'Expand panel'}
            >
              <svg
                className={`w-5 h-5 transition-all duration-300 ease-out transform-gpu
                  ${isOpen ? 'rotate-0' : 'rotate-180'}
                  `}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
              <div className="absolute inset-0 rounded-xl bg-cyan-500/5 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
            </button>
          )}
        </div>
      )}
      {(!collapsible || isOpen) && (
        <div className={`relative overflow-hidden transition-all duration-500 ease-out
          ${isOpen
            ? `opacity-100 ${collapsible ? 'max-h-screen' : 'max-h-none'} unified-padding-panel`
            : 'opacity-0 max-h-0 p-0'
          }
          group-hover:shadow-inner group-hover:shadow-slate-900/10`}>
          {children}
          {/* Enhanced inner glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/8 via-transparent to-purple-500/8 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>
        </div>
      )}
    </div>
  )
}

// Sidebar Component
export function Sidebar({ children, className, collapsed = false }: SidebarProps & { collapsed?: boolean }) {
  return (
    <aside className={cn(
      "relative",
      // Unified background and glass effect
      "unified-bg-secondary unified-glass",
      "unified-border border-r",
      "flex flex-col min-h-screen overflow-y-auto",
      // Simplified shadow and glow effects
      "shadow-xl shadow-slate-900/20",
      "transition-all duration-500 ease-out transform-gpu",
      collapsed ? "w-16" : "w-80",
      className
    )}>
      <div className={cn(
        "relative flex-1 overflow-y-auto transition-all duration-500 ease-out",
        collapsed ? "p-3" : "unified-padding-sidebar"
      )}>
        {/* Simplified background pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500/5 to-transparent"></div>
        </div>

        {/* Content with animations */}
        <div className={cn(
          "relative z-10 transition-all duration-500 ease-out transform-gpu",
          collapsed ? "space-y-3 opacity-0 pointer-events-none scale-95" : "unified-gap-standard h-full opacity-100 scale-100"
        )}>
          {children}
        </div>

        {/* Collapsed state */}
        {collapsed && (
          <div className="relative z-10 unified-gap-standard opacity-100 animate-in fade-in-0 slide-in-from-left-2 duration-300">
            <div className="flex flex-col items-center space-y-4 py-4">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-110 transition-all duration-300 group">
                <span className="text-sm group-hover:rotate-12 transition-transform duration-300">🚀</span>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-110 transition-all duration-300 group">
                <span className="text-sm group-hover:rotate-12 transition-transform duration-300">👁️</span>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:scale-110 transition-all duration-300 group">
                <span className="text-sm group-hover:rotate-12 transition-transform duration-300">⚡</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}

// Main Content Component
export function MainContent({ children, className }: MainContentProps) {
  return (
    <main className={cn(
      "relative min-h-screen overflow-y-auto",
      "unified-padding-content",
      "unified-bg-surface",
      className
    )}>
      <div className="relative z-10">
        {children}
      </div>
    </main>
  )
}

// Dashboard Layout Component
interface DashboardLayoutProps {
  sidebar: React.ReactNode
  main: React.ReactNode
  className?: string
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function DashboardLayout({ sidebar, main, className, collapsed = false, onToggleCollapse }: DashboardLayoutProps) {
  return (
    <div className={cn(
      "relative min-h-screen overflow-hidden",
      // Unified background system
      "unified-bg-primary",
      className
    )}>
      {/* Enhanced Mobile Sidebar Overlay */}
      <div className={cn(
        "fixed inset-0 z-50 lg:hidden transition-all duration-500 ease-out",
        collapsed ? "pointer-events-none" : "pointer-events-auto"
      )}>
        {/* Enhanced Backdrop with blur */}
        <div
          className={cn(
            "absolute inset-0 bg-black/60 backdrop-blur-md transition-all duration-500 ease-out",
            collapsed ? "opacity-0 scale-105" : "opacity-100 scale-100"
          )}
          onClick={onToggleCollapse}
        />

        {/* Mobile Sidebar */}
        <div className={cn(
          "absolute top-0 left-0 h-full w-80",
          "unified-bg-secondary unified-glass",
          "unified-border border-r",
          "shadow-xl shadow-slate-900/20",
          "transition-all duration-500 ease-out transform-gpu",
          collapsed ? "-translate-x-full scale-95" : "translate-x-0 scale-100"
        )}>
          {sidebar}
        </div>
      </div>

      {/* Enhanced Desktop Layout - Now using flexbox for scrolling together */}
      <div className="hidden lg:flex min-h-screen overflow-y-auto">
        {/* Enhanced Desktop Sidebar */}
        <div className={cn(
          "flex-shrink-0 shadow-2xl shadow-cyan-500/10 transition-all duration-500 ease-out",
          collapsed ? "w-16" : "w-80"
        )}>
          {sidebar}
        </div>

        {/* Enhanced Main Content */}
        <div className={cn(
          "flex-1 min-h-screen transition-all duration-500 ease-out transform-gpu"
        )}>
          {main}
        </div>
      </div>

      {/* Enhanced Mobile Sidebar Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className={cn(
          "fixed top-4 left-4 z-50 lg:hidden flex items-center justify-center w-12 h-12",
          "bg-gradient-to-br from-slate-800/90 to-slate-700/80 backdrop-blur-xl",
          "border border-slate-600/50 rounded-2xl",
          "shadow-xl shadow-slate-900/50 hover:shadow-2xl hover:shadow-cyan-500/30",
          "text-slate-300 hover:text-cyan-300 hover:scale-110 active:scale-95",
          "transition-all duration-300 ease-out transform-gpu",
          "group ring-1 ring-slate-500/20 hover:ring-cyan-400/40",
          "before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r",
          "before:from-cyan-500/10 before:to-purple-500/10 before:opacity-0",
          "hover:before:opacity-100 before:transition-opacity before:duration-300"
        )}
        aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
      >
        <svg
          className={cn(
            "w-6 h-6 transition-all duration-300 ease-out relative z-10",
            collapsed ? "rotate-0" : "rotate-180"
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Enhanced Desktop Collapse Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className={cn(
          "fixed top-4 z-40 hidden lg:flex items-center justify-center w-12 h-12",
          "bg-gradient-to-br from-slate-800/90 to-slate-700/80 backdrop-blur-xl",
          "border border-slate-600/50 rounded-2xl",
          "shadow-xl shadow-slate-900/50 hover:shadow-2xl hover:shadow-cyan-500/30",
          "text-slate-300 hover:text-cyan-300 hover:scale-110 active:scale-95",
          "transition-all duration-300 ease-out transform-gpu",
          "group ring-1 ring-slate-500/20 hover:ring-cyan-400/40",
          "before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-r",
          "before:from-cyan-500/10 before:to-purple-500/10 before:opacity-0",
          "hover:before:opacity-100 before:transition-opacity before:duration-300",
          collapsed ? "left-16" : "left-80"
        )}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <svg
          className={cn(
            "w-6 h-6 transition-all duration-300 ease-out relative z-10",
            collapsed ? "rotate-180" : "rotate-0"
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Subtle background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/3 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/3 rounded-full blur-3xl opacity-15 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
    </div>
  )
}